// src/pages/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { DollarSign, ShoppingBag, Activity, Users, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../services/supabase';

const DashboardStyles = () => (
  <style>{`
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 24px;
      margin-bottom: 32px;
    }

    /* Morvin-style stat cards */
    .stat-card {
      background: var(--saas-bg-surface);
      padding: 24px;
      border-radius: 12px;
      border: 1px solid var(--saas-border-color);
      box-shadow: var(--saas-card-shadow);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .stat-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    }
    .stat-icon-circle {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .stat-icon-green  { background: #d1fae5; color: #059669; }
    .stat-icon-blue   { background: #dbeafe; color: #2563eb; }
    .stat-icon-orange  { background: #ffedd5; color: #ea580c; }
    .stat-icon-purple { background: #ede9fe; color: #7c3aed; }

    .stat-info { flex: 1; min-width: 0; }
    .stat-value {
      font-size: 1.75rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      line-height: 1.2;
      margin-bottom: 4px;
    }
    .stat-label {
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--saas-text-secondary);
    }

    /* Dashboard grid layout */
    .dashboard-grid {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 24px;
      margin-bottom: 32px;
    }

    .section-card {
      background: var(--saas-bg-surface);
      padding: 24px;
      border-radius: 12px;
      border: 1px solid var(--saas-border-color);
      box-shadow: var(--saas-card-shadow);
    }
    .section-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--saas-text-primary);
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Activity list - Morvin product tracking style */
    .activity-list { list-style: none; padding: 0; margin: 0; }
    .activity-item {
      display: flex;
      gap: 14px;
      padding: 14px 0;
      border-bottom: 1px solid var(--saas-border-color);
      align-items: center;
    }
    .activity-item:last-child { border-bottom: none; }
    .activity-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #2F7D5E, #48A67C);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      flex-shrink: 0;
    }
    .activity-content {
      flex: 1;
      min-width: 0;
    }
    .activity-content p {
      margin: 0 0 2px 0;
      font-weight: 600;
      color: var(--saas-text-primary);
      font-size: 0.9rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .activity-time {
      font-size: 0.8rem;
      color: var(--saas-text-secondary);
    }
    .activity-amount {
      font-weight: 700;
      color: var(--saas-primary);
      font-size: 0.95rem;
      flex-shrink: 0;
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 60px;
      color: var(--saas-text-secondary);
    }
    .loading-spinner {
      animation: spin 1s linear infinite;
      margin-right: 12px;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .chart-container {
      width: 100%;
      height: 300px;
    }

    @media (max-width: 768px) {
      .dashboard-grid {
        grid-template-columns: 1fr;
      }
      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
      }
      .stat-card {
        padding: 16px;
        gap: 12px;
      }
      .stat-icon-circle {
        width: 40px;
        height: 40px;
      }
      .stat-value {
        font-size: 1.3rem;
      }
    }
  `}</style>
);

const AdminDashboard = () => {
  const { orderRefreshTrigger } = useOutletContext() || {};
  const [stats, setStats] = useState({
    todayRevenue: 0,
    todayOrders: 0,
    pendingOrders: 0,
    totalUsers: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (orderRefreshTrigger > 0) {
      fetchDashboardData();
    }
  }, [orderRefreshTrigger]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: todayOrders, error: todayError } = await supabase
        .from('orders')
        .select('total_amount')
        .gte('created_at', today.toISOString());

      if (todayError) throw todayError;

      const todayRevenue = todayOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
      const todayCount = todayOrders.length;

      const { data: pendingOrders, error: pendingError } = await supabase
        .from('orders')
        .select('id', { count: 'exact' })
        .eq('status', 'pending');

      if (pendingError) throw pendingError;

      const { count: userCount, error: userError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      const totalUsers = userError ? 0 : (userCount || 0);

      // Fetch last 7 days revenue for chart
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      const { data: weekOrders, error: weekError } = await supabase
        .from('orders')
        .select('total_amount, created_at')
        .gte('created_at', sevenDaysAgo.toISOString());

      if (!weekError && weekOrders) {
        const dailyMap = {};
        for (let i = 0; i < 7; i++) {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          const key = d.toISOString().split('T')[0];
          dailyMap[key] = { date: d.toLocaleDateString('en', { month: 'short', day: 'numeric' }), revenue: 0 };
        }
        weekOrders.forEach(order => {
          const key = new Date(order.created_at).toISOString().split('T')[0];
          if (dailyMap[key]) {
            dailyMap[key].revenue += (order.total_amount || 0);
          }
        });
        setRevenueChartData(Object.values(dailyMap));
      }

      // Recent orders
      const { data: recentOrders, error: recentError } = await supabase
        .from('orders')
        .select('id, order_number, contact_info, created_at, status, total_amount')
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentError) throw recentError;

      setStats({
        todayRevenue,
        todayOrders: todayCount,
        pendingOrders: pendingOrders?.length || 0,
        totalUsers
      });

      const activities = recentOrders.map(order => ({
        id: order.id,
        type: 'order',
        title: `新訂單 ${order.order_number} 已建立`,
        description: `客戶: ${order.contact_info?.name || '未知'}`,
        time: formatTimeAgo(order.created_at),
        amount: order.total_amount,
        status: order.status
      }));

      setRecentActivities(activities);

    } catch (error) {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins} 分鐘前`;
    if (diffHours < 24) return `${diffHours} 小時前`;
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays} 天前`;
    return date.toLocaleDateString('zh-TW');
  };

  if (isLoading) {
    return (
      <>
        <DashboardStyles />
        <div className="loading-state">
          <Loader2 size={40} className="loading-spinner" />
          <span>正在載入儀表板...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardStyles />
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon-circle stat-icon-green">
            <DollarSign size={22} />
          </div>
          <div className="stat-info">
            <div className="stat-value">${stats.todayRevenue.toFixed(2)}</div>
            <div className="stat-label">今日營收</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-circle stat-icon-blue">
            <ShoppingBag size={22} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.todayOrders}</div>
            <div className="stat-label">今日訂單數</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-circle stat-icon-orange">
            <Activity size={22} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.pendingOrders}</div>
            <div className="stat-label">待處理訂單</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-circle stat-icon-purple">
            <Users size={22} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">總會員數</div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Revenue chart */}
        <div className="section-card">
          <h3 className="section-title">
            <DollarSign size={18} />
            營收統計（近 7 天）
          </h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2F7D5E" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2F7D5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(value) => [`$${value.toFixed(2)}`, '營收']}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    fontSize: '0.85rem'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2F7D5E"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity list */}
        <div className="section-card">
          <h3 className="section-title">
            <Activity size={18} />
            最近系統動態
          </h3>
          {recentActivities.length === 0 ? (
            <p style={{ color: 'var(--saas-text-secondary)', textAlign: 'center', padding: '20px' }}>
              目前沒有最近活動
            </p>
          ) : (
            <ul className="activity-list">
              {recentActivities.map(activity => (
                <li key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    <ShoppingBag size={18} />
                  </div>
                  <div className="activity-content">
                    <p>{activity.title}</p>
                    <span className="activity-time">{activity.time} - {activity.description}</span>
                  </div>
                  {activity.amount != null && (
                    <span className="activity-amount">${activity.amount.toFixed(2)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
